import {
  RESOURCE_TYPE_REGISTRY,
  type ApiResourceType,
  type ModuleRegistryKey
} from '@/shared/resourceModuleParity'
import type {
  GamificationPage,
  GamificationPageModuleType
} from '@/types/gamificationPage'

import type { GuidanceAction } from './types'

/**
 * `ModuleRegistryKey` → `GamificationPage['module_type']`.
 *
 * São dois vocabulários para a mesma ideia, e eles divergem em um ponto:
 * `resourceModuleParity` chama o feed de `social` e usa
 * `navSlug: 'social-feed'`, enquanto a página o chama de `forum`. Filtrar por
 * `navSlug` — o caminho óbvio — removeria em silêncio toda ação derivada de
 * `Post` em qualquer evento com menu próprio, porque `toNavItem(page)` devolve
 * `slug: page.module_type`.
 *
 * `objectives` é `ModuleRegistryKey` sem página de nav correspondente: mapeia
 * para `null`, e `null` significa "não filtrável", não "inativo".
 */
const MODULE_TYPE_BY_REGISTRY_KEY: Record<
  ModuleRegistryKey,
  GamificationPageModuleType | null
> = {
  journeys: 'journeys',
  missions: 'missions',
  quizzes: 'quizzes',
  library: 'library',
  social: 'forum',
  schedule: 'schedule',
  partners: 'partners',
  objectives: null
}

export const moduleTypeOf = (
  resourceType: ApiResourceType
): GamificationPageModuleType | null =>
  MODULE_TYPE_BY_REGISTRY_KEY[RESOURCE_TYPE_REGISTRY[resourceType].module]

/**
 * Os `module_type` que o evento tem ativos no menu.
 *
 * Reimplementa a derivação de `useHomeBlocks`, que não a exporta. A duplicação
 * de três linhas é deliberada: exportá-la acoplaria o renderer de blocos ao
 * domínio de orientação por uma economia que não se paga.
 */
export const buildActiveModuleTypes = (
  pages: GamificationPage[] | undefined
): Set<string> =>
  new Set(
    (pages ?? [])
      .filter(page => page.active && page.module_type)
      .map(page => page.module_type as string)
  )

/**
 * Remove as ações cujo módulo o produtor desativou.
 *
 * A trava é `pagesLoaded`, **não** o tamanho do conjunto. As duas condições
 * parecem a mesma e não são: um evento cujo menu só tem páginas avulsas
 * (`module_type: null`), ou cujas páginas de módulo estão todas inativas, tem
 * `pages` carregado e conjunto vazio. Filtrar por tamanho deixaria passar
 * todas as ações justamente nesse evento — o oposto do requisito.
 *
 * Com as páginas ainda não carregadas, não filtra: elas chegam logo depois do
 * primeiro paint, e esconder cedo é pior que mostrar. É a mesma trava que
 * `useHomeBlocks` aplica aos blocos, e ela guarda por `!pages || !pages.length`.
 */
export const filterByActiveModules = (
  actions: GuidanceAction[],
  activeModuleTypes: Set<string>,
  pagesLoaded: boolean
): GuidanceAction[] => {
  if (!pagesLoaded) return actions

  return actions.filter(action => {
    const moduleType = moduleTypeOf(action.resourceType)
    if (moduleType === null) return true

    return activeModuleTypes.has(moduleType)
  })
}
